import WKT from 'ol/format/WKT';
import { getArea, getLength } from 'ol/sphere';
import { Vector as VectorSource } from 'ol/source';
import * as olExtent from 'ol/extent';
/* We can disable import/no-cycle here because we know that
  api only depends on the User module. */
// eslint-disable-next-line import/no-cycle
import api from '@/api';

const defaultState = () => ({
  currentNotificationId: null,
  activities: [],
  importActivityIds: [],
  activityExtent: null,
  activityTypes: {
    harvest: {
      name: 'Harvest',
      url: 'harvest',
      loadMutation: 'LOAD_HARVEST_FEATURES',
      deleteMutation: 'DELETE_HARVEST_FEATURE',
    },
    roadConstruction: {
      name: 'Road Construction',
      url: 'roadConstruction',
      loadMutation: 'LOAD_ROAD_FEATURES',
      deleteMutation: 'DELETE_ROAD_FEATURE',
    },
    waterCrossing: {
      name: 'Water Crossing',
      url: 'waterCrossing',
      loadMutation: 'LOAD_CROSSING_FEATURES',
      deleteMutation: 'DELETE_CROSSING_FEATURE',
    },
  },
  nextId: 1,
  currentActivityId: null,
  drawing: false,
  editing: false,
  harvestSource: new VectorSource(),
  roadConstructionSource: new VectorSource(),
  waterCrossingSource: new VectorSource(),
  loading: false,
  currentActivityError: null,
  submitting: false,
});

const localState = defaultState();

const actions = {
  async fetchActivities({ commit, rootState, state }) {
    // Already loaded for this notification, state should be up to date
    if (rootState.Notification.notification.id === state.currentNotificationId) return;
    commit('RESET_STATE');
    commit('SET_LOADING', true);
    await api.get(`notification/${rootState.Notification.notification.id}/activities`)
      .then((response) => {
        const loadActivities = (activities, type) => {
          if (!activities) return;
          const features = activities.map((activity) => {
            const feature = new WKT().readFeature(activity.shapeWkt);
            feature.setId(activity.activityId);
            return feature;
          });
          commit('ADD_ACTIVITIES', activities.map(activity => ({
            ...activity,
            type,
            size: 'N/A',
          })));
          features.forEach(f => commit('CALCULATE_FEATURE_VALUES', f));
          commit(type.loadMutation, features);
        };

        loadActivities(response.data.harvests, state.activityTypes.harvest);
        loadActivities(response.data.roadConstructions, state.activityTypes.roadConstruction);
        loadActivities(response.data.waterCrossings, state.activityTypes.waterCrossing);

        commit('SET_NEXT_ID');
        commit('SET_EXTENT_TO_SOURCE');
        commit('SET_CURRENT_NOTIFICATION_ID', rootState.Notification.notification.id);
        commit('SET_LOADING', false);
      })
      .catch((error) => {
        commit('SET_LOADING', false);
        throw error;
      });
  },
  addActivity({ state, commit }, type) {
    commit('SET_DRAWING', true);
    commit('SET_EDITING', true);
    const activityId = state.nextId;
    commit('NEXT_ID');
    commit('SET_CURRENT_ACTIVITY', activityId);
    commit('ADD_ACTIVITIES', [{
      activityId,
      notificationId: state.currentNotificationId,
      type,
      structurePermanence: null,
      structureTypeId: null,
      size: 'N/A',
      shapeWkt: '',
    }]);
  },
  importFeatures({ state, commit }, {
    type, features, defaultstructurePermanence, defaultstructureTypeId,
  }) {
    commit('SET_CURRENT_ACTIVITY', null);
    commit('SET_CURRENT_ACTIVITY_ERROR', null);
    commit('SET_DRAWING', false);
    commit('SET_EDITING', true);
    // eslint-disable-next-line no-unused-vars
    features.forEach((f) => {
      const activityId = state.nextId;
      commit('ADD_ACTIVITIES', [{
        activityId,
        notificationId: state.currentNotificationId,
        type,
        structurePermanence: defaultstructurePermanence,
        structureTypeId: defaultstructureTypeId,
        size: 'N/A',
        shapeWkt: '',
      }]);
      commit('NEXT_ID');
      commit('ADD_IMPORT_ACTIVITY_ID', activityId);
    });
  },
  removeCurrentActivity({ commit }) {
    commit('SET_DRAWING', false);
    commit('SET_EDITING', false);
    commit('REMOVE_CURRENT_ACTIVITY');
    commit('SET_CURRENT_ACTIVITY', null);
    commit('SET_CURRENT_ACTIVITY_ERROR', null);
    commit('SET_NEXT_ID');
  },
  removeImportActivity({ state, commit }, activityId) {
    commit('SET_DRAWING', false);
    commit('REMOVE_ACTIVITY', activityId);
    commit('REMOVE_IMPORT_ACTIVITY_ID', activityId);
    if (state.currentActivityId === activityId) {
      commit('SET_CURRENT_ACTIVITY', null);
      commit('SET_CURRENT_ACTIVITY_ERROR', null);
    }
    if (state.importActivityIds.length === 0) {
      commit('SET_EDITING', false);
    }
  },
  async saveActivity({ commit, state }, feature) {
    if (state.submitting) return;
    commit('SET_SUBMITTING', true);
    commit('SET_CURRENT_ACTIVITY', feature.getId());
    commit('SET_CURRENT_ACTIVITY_ERROR', null);
    const activity = state.activities.find(a => a.activityId === feature.getId());
    commit('CALCULATE_FEATURE_VALUES', feature);
    await api.post(`${activity.type.url}/create`, activity)
      .then((response) => {
        activity.id = response.data;
        commit('SET_CURRENT_ACTIVITY', null);
        commit('SET_EXTENT_TO_SOURCE');
        commit('SET_DRAWING', false);
        commit('SET_CURRENT_ACTIVITY_ERROR', null);
        commit('SET_SUBMITTING', false);
        commit('REMOVE_IMPORT_ACTIVITY_ID', activity.activityId);
        if (state.importActivityIds.length === 0) {
          commit('SET_EDITING', false);
        }
      })
      .catch((error) => {
        if (error.response.data === 'Self-intersection') {
          commit(
            'SET_CURRENT_ACTIVITY_ERROR',
            'Harvest boundary line intersects itself.  Please fix or click Cancel to start over.',
          );
        } else {
          commit('SET_CURRENT_ACTIVITY_ERROR', error.response.data);
        }
        commit('SET_SUBMITTING', false);
        throw error;
      });
  },
  async delete({ commit }, activity) {
    commit('SET_SUBMITTING', true);
    await api.delete(`${activity.type.url}/${activity.id}`)
      .then(() => {
        commit('REMOVE_ACTIVITY', activity.activityId);
        commit(activity.type.deleteMutation, activity.activityId);
        commit('SET_NEXT_ID');
        commit('SET_EXTENT_TO_SOURCE');
        commit('SET_SUBMITTING', false);
      })
      .catch((error) => {
        commit('SET_SUBMITTING', false);
        throw error;
      });
  },
  switchToEdit({ commit }) {
    commit('SET_DRAWING', false);
    commit('SET_EDITING', true);
  },
  unload({ commit }) {
    commit('RESET_STATE');
  },
};

const mutations = {
  SET_DRAWING(state, drawing) {
    state.drawing = drawing;
  },
  SET_EDITING(state, editing) {
    state.editing = editing;
  },
  NEXT_ID(state) {
    state.nextId += 1;
  },
  SET_NEXT_ID(state) {
    state.nextId = Math.max(...state.activities.map(a => a.activityId), 0) + 1;
  },
  ADD_ACTIVITIES(state, activities) {
    state.activities.push(...activities);
    state.activities.sort((a, b) => a.activityId - b.activityId);
  },
  REMOVE_ACTIVITY(state, activityId) {
    const index = state.activities.findIndex(a => a.activityId === activityId);
    state.activities.splice(index, 1);
  },
  REMOVE_CURRENT_ACTIVITY(state) {
    const currentIndex = state.activities.findIndex(a => a.activityId === state.currentActivityId);
    state.activities.splice(currentIndex, 1);
  },
  SET_CURRENT_ACTIVITY(state, activityId) {
    state.currentActivityId = activityId;
  },
  ADD_IMPORT_ACTIVITY_ID(state, activityId) {
    state.importActivityIds.push(activityId);
  },
  REMOVE_IMPORT_ACTIVITY_ID(state, activityId) {
    const index = state.importActivityIds.findIndex(id => id === activityId);
    state.importActivityIds.splice(index, 1);
  },
  CALCULATE_FEATURE_VALUES(state, feature) {
    const activity = state.activities.find(a => a.activityId === feature.getId());
    let { size } = activity;
    if (activity.type === state.activityTypes.harvest) {
      const acres = getArea(feature.getGeometry()) * 0.000247105;
      size = `${(Math.round(acres * 10) / 10).toLocaleString()} Acres`;
    } else if (activity.type === state.activityTypes.roadConstruction) {
      const feet = getLength(feature.getGeometry()) * 3.28084;
      size = `${Math.round(feet).toLocaleString()} Feet`;
    }
    activity.size = size;
    activity.shapeWkt = new WKT().writeFeature(feature);
  },
  LOAD_HARVEST_FEATURES(state, features) {
    state.harvestSource.addFeatures(features);
  },
  LOAD_ROAD_FEATURES(state, features) {
    state.roadConstructionSource.addFeatures(features);
  },
  LOAD_CROSSING_FEATURES(state, features) {
    state.waterCrossingSource.addFeatures(features);
  },
  DELETE_HARVEST_FEATURE(state, featureId) {
    state.harvestSource.removeFeature(state.harvestSource.getFeatureById(featureId));
  },
  DELETE_ROAD_FEATURE(state, featureId) {
    state.roadConstructionSource.removeFeature(
      state.roadConstructionSource.getFeatureById(featureId),
    );
  },
  DELETE_CROSSING_FEATURE(state, featureId) {
    state.waterCrossingSource.removeFeature(
      state.waterCrossingSource.getFeatureById(featureId),
    );
  },
  SET_CURRENT_NOTIFICATION_ID(state, id) {
    state.currentNotificationId = id;
  },
  SET_EXTENT_TO_SOURCE(state) {
    if (state.activities.length) {
      const extent = state.harvestSource.getExtent();
      olExtent.extend(extent, state.roadConstructionSource.getExtent());
      olExtent.extend(extent, state.waterCrossingSource.getExtent());
      const buffer = olExtent.getHeight(extent) * 0.1;
      state.activityExtent = olExtent.buffer(extent, buffer);
    } else {
      state.activityExtent = null;
    }
  },
  RESET_STATE(state) {
    Object.assign(state, defaultState());
  },
  SET_CURRENT_ACTIVITY_ERROR(state, error) {
    state.currentActivityError = error;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_SUBMITTING(state, submitting) {
    state.submitting = submitting;
  },
};

const getters = {
  currentActivity(state) {
    return state.activities.find(a => a.activityId === state.currentActivityId);
  },
  harvestExists(state) {
    return state.activities.some(activity => activity.type === state.activityTypes.harvest);
  },
  roadExists(state) {
    return state.activities
      .some(activity => activity.type === state.activityTypes.roadConstruction);
  },
  waterCrossingExists(state) {
    return state.activities
      .some(activity => activity.type === state.activityTypes.waterCrossing);
  },
  getActivityPermanence(state, storeGetters) {
    return (activity) => {
      if (!storeGetters.activityPermanenceRequired(activity)) {
        return 'N/A';
      }
      return activity.structurePermanence ? 'Permanent' : 'Temporary';
    };
  },
  activityPermanenceRequired(state) {
    return activity => activity.type === state.activityTypes.roadConstruction
        || activity.type === state.activityTypes.waterCrossing;
  },
  activityPermanenceRequiredByType(state) {
    return type => type === state.activityTypes.roadConstruction
        || type === state.activityTypes.waterCrossing;
  },
  structureTypeRequired(state) {
    return activity => activity.type === state.activityTypes.waterCrossing;
  },
  structureTypeRequiredByType(state) {
    return type => type === state.activityTypes.waterCrossing;
  },
  sizeCalculated(state) {
    return activity => activity.type === state.activityTypes.roadConstruction
      || activity.type === state.activityTypes.harvest;
  },
};

export default {
  actions,
  getters,
  mutations,
  state: localState,
  namespaced: true,
};
